import { useState, useMemo, useEffect } from "react";

import "./stylesNewRole.css";

import CustomInput from "../../../CommonComponents/CustomInput/CustomInput";
import PermissionsSection from "../Permissions/PermissionsSection/PermissionsSection";
import ActionsGrid from "../Permissions/ActionsGrid/ActionsGrid";
import { buildDefaultPermissionStateFromSchema } from "../../../../utils/roleUtils";

import { CheckOutlined, UserAddOutlined } from "@ant-design/icons";
import { AppStrings } from "../../../../utils/constants/AppStrings";

const DEFAULT_COLORS = [
  { value: "#FFFFFF", label: "White", color: "#FFFFFF" },
  { value: "#3498DB", label: "Blue", color: "#3498DB" },
  { value: "#EC4899", label: "Pink", color: "#EC4899" },
  { value: "#D9D9D9", label: "Gray", color: "#D9D9D9" },
  { value: "#000000", label: "Black", color: "#000000" },
  { value: "#FF5733", label: "Orange", color: "#FF5733" },
  { value: "#FFD700", label: "Gold", color: "#FFD700" },
  { value: "#FFA500", label: "Orange", color: "#FFA500" },
  { value: "#6b7280", label: "Slate", color: "#6b7280" },
  { value: "#22c55e", label: "Green", color: "#22c55e" },
  { value: "#9B59B6", label: "Purple", color: "#9B59B6" },
];

function buildDefaultPermissions(schema) {
  return buildDefaultPermissionStateFromSchema(schema);
}

function buildDefaultActions(schema) {
  const actions = {};
  (schema?.available_actions ?? []).forEach((a) => {
    actions[a.key] = false;
  });
  return actions;
}

function NewRole({
  schema,
  colorOptions,
  usedColors = [],
  handleChangeRoleName,
  handleChangeRoleColor,
  handleSaveNewRole,
  handleCloseNewRole,
  onDraftChange,
}) {
  const [selectedColor, setSelectedColor] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [permissions, setPermissions] = useState(() => buildDefaultPermissions(schema));
  const [actions, setActions] = useState(() => buildDefaultActions(schema));
  const [readOnlyAccess, setReadOnlyAccess] = useState(false);

  useEffect(() => {
    if (typeof onDraftChange === "function") {
      onDraftChange(permissions, actions, readOnlyAccess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when permissions/actions/read_only_access change
  }, [permissions, actions, readOnlyAccess]);

  const usedColorsLower = useMemo(() => usedColors.map((c) => (c || "").toLowerCase()), [usedColors]);

  const actionsList = schema?.available_actions ?? [];
  const permissionCategories = schema?.available_permissions ?? [];

  const handleSelectColor = (color) => {
    setSelectedColor(color.value);
    handleChangeRoleColor(color.color);
  };

  const handleChangeRoleNameInput = (e) => {
    const v = e?.target?.value ?? "";
    setRoleName(v);
    handleChangeRoleName(v);
  };

  const handleActionToggle = (actionKey) => {
    setActions((prev) => ({ ...prev, [actionKey]: !prev[actionKey] }));
  };

  const handleBulkActionChange = (nextActions) => {
    setActions(nextActions);
  };

  const handleReadOnlyAccessChange = (next) => {
    setReadOnlyAccess(Boolean(next));
  };

  const handlePermissionsChange = (updatedPermissions) => {
    setPermissions(updatedPermissions);
  };

  const canSave = Boolean(roleName?.trim() && selectedColor != null);
  const isColorUsed = (colorValue) => usedColorsLower.includes((colorValue || "").toLowerCase());

  const handleSave = () => {
    if (!canSave) return;
    if (isColorUsed(selectedColor)) return;
    handleSaveNewRole(roleName.trim(), selectedColor, permissions, actions, readOnlyAccess);
  };

  const handleCancel = () => {
    handleCloseNewRole();
  };

  const colors = colorOptions?.length ? colorOptions : DEFAULT_COLORS;

  return (
    <div className="staff-management-new-role-container">
      <div className="staff-management-new-role-header">
        <h2 className="staff-management-new-role-header-title">
          {AppStrings.NEW_ROLE}
        </h2>
      </div>
      <div className="staff-management-new-role-body">
        <div className="staff-management-new-role-body-input-container">
          <span className="staff-management-new-role-body-input-label">
            {AppStrings.ROLE_NAME}
          </span>
          <CustomInput
            placeholder={AppStrings.ROLE_NAME_PLACEHOLDER}
            className="staff-management-new-role-body-input-input"
            prefix={<UserAddOutlined />}
            value={roleName}
            onChange={handleChangeRoleNameInput}
          />
        </div>
        <div className="staff-management-new-role-body-color-container">
          <span className="staff-management-new-role-body-color-label">
            {AppStrings.ROLE_COLOR}
          </span>
          <div className="staff-management-new-role-body-color-picker">
            {colors.map((color) => {
              const used = isColorUsed(color.value);
              return (
                <div
                  key={color.value}
                  className="staff-management-new-role-body-color-picker-color"
                  style={{
                    backgroundColor: color.color,
                    opacity: used ? 0.3 : 1,
                    cursor: used ? "not-allowed" : "pointer",
                  }}
                  title={used ? "This color is already used by another role" : undefined}
                  onClick={() => !used && handleSelectColor(color)}
                  role="button"
                  tabIndex={used ? -1 : 0}
                  onKeyDown={(e) => e.key === "Enter" && !used && handleSelectColor(color)}
                >
                  {selectedColor === color.value && (
                    <CheckOutlined style={color.value === "#000000" ? { color: "#FFFFFF" } : {}} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="staff-management-new-role-body-columns">
          <PermissionsSection
            categories={permissionCategories}
            permissions={permissions}
            onPermissionsChange={handlePermissionsChange}
          />
          <ActionsGrid
            actions={actionsList}
            values={actions}
            onToggle={handleActionToggle}
            onBulkChange={handleBulkActionChange}
            readOnlyAccess={readOnlyAccess}
            onReadOnlyAccessChange={handleReadOnlyAccessChange}
          />
        </div>
        <div className="staff-management-new-role-body-actions-buttons">
          <button className="staff-management-new-role-body-actions-buttons-button" onClick={handleCancel}>
            {AppStrings.CANCEL}
          </button>
          <button
            type="button"
            className="staff-management-new-role-body-actions-buttons-button"
            disabled={!canSave || isColorUsed(selectedColor)}
            onClick={handleSave}
          >
            {AppStrings.SAVE}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewRole;
